import React, { useEffect, useMemo, useState } from "react";
import { Button, Flex, Modal, Space, Row, Col, Popover, Badge, SelectProps, Tag, Tooltip } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import {
  mdiFileDocumentOutline,
  mdiInformationVariantCircle,
  mdiMapMarker,
  mdiFire,
  mdiMarkerCheck,
  mdiInformation,
} from "@mdi/js";
import Icon from "@mdi/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Document, Page } from "react-pdf";
import Legends from "./Legends";
import Paragraph from "antd/es/typography/Paragraph";
import { Itinerary } from "../../interfaces/itinerary.interface";
import { NEW_BADGE_ICON_COLOR } from "../../utils/constants";
import ListApi from "../../api-services/list-api.service";
import { EX_END_FULL_LOCATION } from "../../enums/departure_location.enum";

const Listings = ({ itineraries = [], itineraryIdentifier = "id" }) => {
  const listApiServiceInstance = useMemo(() => new ListApi, []);

  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [itineraryModalOpen, setItineraryModalOpen] = useState(false);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [descriptionData, setDescriptionData] = useState("");
  const [fileUrlData, setFileUrlData] = useState("");
  const [itineraryData, setItineraryData] = useState(null);
  const [departureDatesData, setDepartureDatesData] = useState<Array<{ type: string; date: string }>>([]);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState("");
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(1000);
  const [visibleCount, setVisibleCount] = useState(12);
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const [brands, setBrands] = useState<SelectProps['options']>([]);

  useEffect(() => {
    const fetchBrands = async () => {
      const selectedBrands = searchParams.get("brands")?.split(",");
      const brands = (await listApiServiceInstance.getBrands())?.flatMap(({options}) => options);
      const remainingBrands = brands.filter(({value})=> !selectedBrands?.includes(value));
      setBrands(remainingBrands);
    }

    fetchBrands();
  }, [listApiServiceInstance])

  const showModal = (modalSetter, propSetter, value) => {
    modalSetter(true);
    propSetter(value);
  };

  const closeModal = (modalSetter) => {
    modalSetter(false);
  };

  const openDepartureDatesModal = (itinerary, departureDates) => {
    setDateModalOpen(true);
    setItineraryData(itinerary);
    setDepartureDatesData(departureDates);
  };

  const getItineraryUrl = (itinerary, departureDate = itinerary?.departureDate?.[0]) => {
    if (!itinerary) return "";

    const query = new URLSearchParams();
    if (type) query.set("type", type);
    if (departureDate) query.set("depart_date", departureDate);

    const identifier = itineraryIdentifier && itineraryIdentifier == "code" ? itinerary.code : itinerary.id;
    const queryString = query.toString();
    return `/web/itinerary/${identifier}${queryString ? `?${queryString}` : ""}`;
  };

  const itineraryLinkByDate = (itinerary, date) =>
    getItineraryUrl(itinerary, date);

  const onDocumentLoadSuccess = async (pdfDoc) => {
    const page = await pdfDoc.getPage(1);
    setNumPages(pdfDoc["_pdfInfo"]["numPages"]);
    setPageWidth(page.view[2] + 50);
  };

  const departureDate = (type, date) => (
    <span
      className={`departure-date ${type} ${selectedDepartureDate === date && "selected"}`}
      onClick={() => setSelectedDepartureDate(date)}
    >
      {date}
    </span>
  );

  const getCategoryImage = (category: string) => {
    if (!category) return "";
    const categoryMap = {
      "GD Standard": "/iceholidays-assets/images/icons/gd.png",
      FS: "/iceholidays-assets/images/icons/fs.png",
      "GD Premium": "/iceholidays-assets/images/icons/premium.png",
      "GD Domestic": "/iceholidays-assets/images/icons/gd_domestic.png",
      "GD Signature": "/iceholidays-assets/images/icons/gd_signature.png",
      "GD FLY CRUISE": "/iceholidays-assets/images/icons/fly_cruise.png",
    };
    if (categoryMap[category]) return categoryMap[category];
    if (category.includes("CRUISE"))
      return "/iceholidays-assets/images/icons/cruise.png";
    if (category.includes("GD In Depth"))
      return "/iceholidays-assets/images/icons/gd.png";
    return "";
  };

  const getCategoryBranding = (category: string) => {
    if (!category) return "";
    const categoryMap = {
      "GD Standard": "GD Classic offers hassle-free classic tours to popular destinations, with iconic attractions, well-paced itineraries, quality stays, and reliable support for a smooth and enjoyable journey.",
      "FS": "FS Holidays offers value-for-money tours with flexible itineraries, combining must-see attractions and free time for a relaxed and memorable journey.",
      "GD Signature": "The Signature Tours crafts distinctive luxury journeys with boutique stays, cultural experiences, and refined service.",
      "GD FLY CRUISE": "GD FlyCruise offers global cruise journeys with flights, accommodation, and land experiences, supported by a tour leader for a seamless experience.",
    };
    if (categoryMap[category]) return categoryMap[category];
    if (category.includes("CRUISE"))
      return "GD World Cruises offers exciting cruise packages from Port Klang, Malacca, and Singapore, featuring cruise cabins and themed cruises for your perfect sea getaway.";
    if (category.includes("GD In Depth"))
      return "GD In-Depth offers immersive journeys beyond sightseeing, connecting you with local culture and daily life.";
    return "";
  };

  const hasDropShadow = (category) => {
    if (!category) return false;
    return category === "GD FLY CRUISE";
  };

  const isCruiseTour = (category: string) => {
    if (!category) return false;
    return category === "CRUISE";
  };

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 12);
  };

  const visibleItineraries = itineraries.slice(0, visibleCount);
  const hasMore = visibleCount < itineraries.length;

  // --- Helper to render status badges ---
  // Now accepts computed flags and uses direct image paths
  const renderStatusBadges = (
    isGuaranteed: boolean,
    isSellingFast: boolean,
  ) => {
    const badges = [];

    // 1. Selling Fast (Almost Guaranteed)
    // if (isSellingFast) {
    //   badges.push(
    //     <div className="status_badge_item" key="selling_fast">
    //       <div className="status_badge_icon">
    //         <Icon path={mdiFire} size="20px" color="#DC1A22" />
    //       </div>
    //       <span className="status_badge_text">Selling Fast</span>
    //     </div>,
    //   );
    // }

    // 2. Guaranteed Departure
    if (isGuaranteed) {
      badges.push(
        <div className="status_badge_item" key="guaranteed">
          <div className="status_badge_icon">
            <Icon path={mdiMarkerCheck} size="18px" color="#52c41a" />
          </div>
          <span className="status_badge_text">Guaranteed Departure</span>
        </div>,
      );
    }

    if (badges.length === 0) return null;

    return <div className="tour_badge_right">{badges}</div>;
  };

  const appendBrandsToSearchParams = (selected) => {
    const params = new URLSearchParams(window.location.search);
    const selectedBrands = params.get("brands");
    if(selectedBrands && selectedBrands.length > 0){
      const selectedArr = selectedBrands.split(",");
      selectedArr.push(selected);
      params.set("brands", selectedArr.join(","));
      window.location.search = params.toString();
    }
  }

  const resetSearchParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("brands", "GD_Standard");
    window.location.search = params.toString();
  }

  return (
    <div className="listings">
      <div id="tours">
        <Row gutter={[20, 20]}>
          {visibleItineraries.map((itinerary: Itinerary) => {
            // --- LOGIC: Check availability dates ---
            const departureDates = itinerary.departureDate || [];

            const isGuaranteed = isCruiseTour(itinerary.category)
              ? false
              : departureDates.some((date) =>
                  itinerary.guranteedDepartureDates?.includes(date),
                );

            const isSellingFast = departureDates.some((date) =>
              itinerary.almostGuaranteedDepartureDates?.includes(date),
            );

            const isNew = !!itinerary.isNew;

            // --- Categories & Location ---
            const categoryImage = getCategoryImage(itinerary.category);
            const categoryBranding = getCategoryBranding(itinerary.category);
            const mainLocation =
              itinerary.departureLocations &&
              itinerary.departureLocations.length > 0
                ? itinerary.departureLocations[0]
                : null;

            const STAR_RATING = itinerary.category_star?.sort((a, b) => a - b).join("~") || "";
            const tourCard = (
              <div className="tour tour-card">
                {/* Image Area */}
                <div className="tour_image_wrapper">
                  <div
                    className="tour_image"
                    style={{ backgroundImage: `url(${itinerary.images[0]})` }}
                  />

                  {/* Top Left Badge (Logo) */}
                  {categoryImage && (
                    <Popover
                      className="badge-popover-container"
                      color="#000"
                      content={
                        <div className="badge-popover-content">
                          <span>{categoryBranding}</span>
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="tour_badge_left">
                        <div className="tour_badge_left_inner">
                          <img
                            className={
                              !hasDropShadow(itinerary.category)
                                ? "brand_logo_drop_shadow"
                                : ""
                            }
                            src={categoryImage}
                            alt={itinerary.category}
                          />
                        </div>
                      </div>
                    </Popover>
                  )}

                  {/* Top Right Status Badges */}
                  {renderStatusBadges(
                    isGuaranteed,
                    isSellingFast,
                  )}

                  <div
                    className="info_icon_btn"
                    onClick={(e) => {
                      e.preventDefault();
                      showModal(
                        setDescriptionModalOpen,
                        setDescriptionData,
                        itinerary.description,
                      );
                    }}
                  >
                    <Icon
                      path={mdiInformation}
                      size={1}
                    />
                  </div>
                </div>

                {/* Content Area */}
                <div className="tour_content">
                  <div>
                    {/* Meta Row */}
                    <div className="tour_meta_row">
                      <div className="tour_meta_left">
                        <div className="tour_meta_item">
                          <Icon path={mdiMapMarker} size={0.6} color="#333" />
                          <span>{itinerary.country}</span>
                        </div>
                        <div
                          className="tour_meta_item tour_meta_item--code"
                          onClick={(e) => {
                            e.preventDefault();
                            // if (itinerary.fileUrlCopy) {
                            showModal(
                              setItineraryModalOpen,
                              setFileUrlData,
                              itinerary.fileUrlCopy,
                            );
                            // }
                          }}
                        >
                          <Icon
                            path={mdiFileDocumentOutline}
                            size={0.6}
                            color="#d32f2f"
                          />
                          <span className="tour_meta_item--code-label">
                            {itinerary.code}
                          </span>
                        </div>
                      </div>

                      <div className="tour_meta_right">
                        {mainLocation && (
                          <Tooltip title={EX_END_FULL_LOCATION[mainLocation] ?? mainLocation}>
                            <span className="location_badge" style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>
                              {EX_END_FULL_LOCATION[mainLocation] ?? mainLocation}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {/* Title with Tooltip */}
                    <Paragraph
                      className="tour_title"
                      ellipsis={{
                        rows: 4,
                        tooltip: itinerary.caption,
                      }}
                    >
                      {itinerary.caption}
                    </Paragraph>
                  </div>

                  {/* Footer */}
                  <div className="tour_footer">
                    <div className="price_block">
                      <span className="price_label">From</span>
                      <span className="price_value">{itinerary.price}</span>
                    </div>
                    <Link
                      className="select_btn"
                      to={getItineraryUrl(itinerary)}
                    >
                      Select
                    </Link>
                  </div>
                </div>
              </div>
            );

            return (
              <Col
                key={itinerary.id}
                xs={24}
                sm={12}
                md={8}
                lg={6}
                className="tour-col"
              >
                {isNew ? (
                  <Badge.Ribbon
                    text="New"
                    color={NEW_BADGE_ICON_COLOR}
                    rootClassName="tour-ribbon-wrapper"
                  >
                    {tourCard}
                  </Badge.Ribbon>
                ) : tourCard}
              </Col>
            );
          })}
        </Row>

        <Flex justify="center" align="center" vertical gap={10} style={{ marginBottom: 20, marginTop: 40 }}>
          {hasMore && (
            <Button
              type="primary"
              size="large"
              onClick={handleShowMore}
              style={{ minWidth: 200 }}
            >
              Show More
            </Button>
          )}
          {
            brands && brands.length > 0 && (
              <>
              <div>Also available from other brands:</div>
              <Space wrap style={{justifyContent: "center"}}>
                {
                  brands.map(b => <Tooltip title={`Add ${b.label} to your search results`}>
                    <Tag className="brand-tag" key={b.value} onClick={()=>appendBrandsToSearchParams(b.value)}> {b.label} </Tag>
                  </Tooltip>)
                }
              </Space>
              </>
            )
          }
        </Flex>
      </div>

      <Modal
        title="Description"
        open={descriptionModalOpen}
        onCancel={() => closeModal(setDescriptionModalOpen)}
        footer={null}
        width={1000}
        centered
        className="tour_details_description"
      >
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
          {descriptionData}
        </Markdown>
      </Modal>

      <Modal
        title="Itinerary"
        width="auto"
        maskClosable={false}
        centered
        className="tour_details_itinerary"
        open={itineraryModalOpen}
        onCancel={() => closeModal(setItineraryModalOpen)}
        footer={
          fileUrlData ? (
            <Button href={fileUrlData} target="_blank" type="primary">
              Download
            </Button>
          ) : null
        }
      >
        <div id="itinerary-file">
          <Document
            file={fileUrlData}
            onLoadSuccess={onDocumentLoadSuccess}
            error={"There is no itinerary available."}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <Page key={`page_${index + 1}`} pageNumber={index + 1} />
            ))}
          </Document>
        </div>
      </Modal>

      <Modal
        title={
          <Flex vertical>
            <div className="custom-modal-header-title">Departure Date(s)</div>
            <div className="custom-modal-header-legends">
              <Legends />
            </div>
          </Flex>
        }
        footer={
          <Button
            disabled={selectedDepartureDate === ""}
            href={itineraryLinkByDate(itineraryData, selectedDepartureDate)}
          >
            Confirm
          </Button>
        }
        open={dateModalOpen}
        onCancel={() => closeModal(setDateModalOpen)}
        width={940}
        centered
        className="tour_details_all_dates"
      >
        <div className="date-selector">
          <Space size={8} wrap>
            {departureDatesData &&
              departureDatesData.map((ddate, index) =>
                departureDate(ddate.type, ddate.date),
              )}
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default Listings;
